import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  IconButton,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { useTheme } from "../../theming";
import { useResponsive } from "../../contexts/ResponsiveContext";

const BusinessContextSection = ({ data, isEditing, onSave }) => {
  const theme = useTheme();
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const [editedData, setEditedData] = useState(
    data || {
      name: "",
      description: "",
      valueProposition: "",
      keyDifferentiators: [],
      targetAudience: "",
      brandVoice: "",
    }
  );

  useEffect(() => {
    if (data) {
      setEditedData(data);
    }
  }, [data]);

  const handleChange = (field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDifferentiatorChange = (index, value) => {
    const newDifferentiators = [...editedData.keyDifferentiators];
    newDifferentiators[index] = value;
    handleChange("keyDifferentiators", newDifferentiators);
  };

  const addDifferentiator = () => {
    handleChange("keyDifferentiators", [...editedData.keyDifferentiators, ""]);
  };

  const removeDifferentiator = (index) => {
    const newDifferentiators = editedData.keyDifferentiators.filter(
      (_, i) => i !== index
    );
    handleChange("keyDifferentiators", newDifferentiators);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(editedData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Typography
          variant="h5"
          sx={{
            color: theme.primaryColor,
            fontWeight: "bold",
          }}
        >
          Business Context
        </Typography>

        <TextField
          fullWidth
          label="Business Name"
          value={editedData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          // disabled={!isEditing}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: `${theme.primaryColor}40`,
              },
              "&:hover fieldset": {
                borderColor: theme.primaryColor,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.primaryColor,
              },
            },
            "& .MuiInputLabel-root": {
              color: theme.textColor,
            },
            "& .MuiInputBase-input": {
              color: theme.textColor,
            },
          }}
        />

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Business Description"
          value={editedData.description}
          onChange={(e) => handleChange("description", e.target.value)}
          disabled={!isEditing}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: `${theme.primaryColor}40`,
              },
              "&:hover fieldset": {
                borderColor: theme.primaryColor,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.primaryColor,
              },
            },
            "& .MuiInputLabel-root": {
              color: theme.textColor,
            },
            "& .MuiInputBase-input": {
              color: theme.textColor,
            },
          }}
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Value Proposition"
          value={editedData.valueProposition}
          onChange={(e) => handleChange("valueProposition", e.target.value)}
          disabled={!isEditing}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: `${theme.primaryColor}40`,
              },
              "&:hover fieldset": {
                borderColor: theme.primaryColor,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.primaryColor,
              },
            },
            "& .MuiInputLabel-root": {
              color: theme.textColor,
            },
            "& .MuiInputBase-input": {
              color: theme.textColor,
            },
          }}
        />

        <TextField
          fullWidth
          label="Target Audience"
          value={editedData.targetAudience}
          onChange={(e) => handleChange("targetAudience", e.target.value)}
          disabled={!isEditing}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: `${theme.primaryColor}40`,
              },
              "&:hover fieldset": {
                borderColor: theme.primaryColor,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.primaryColor,
              },
            },
            "& .MuiInputLabel-root": {
              color: theme.textColor,
            },
            "& .MuiInputBase-input": {
              color: theme.textColor,
            },
          }}
        />

        <TextField
          fullWidth
          label="Brand Voice"
          value={editedData.brandVoice}
          onChange={(e) => handleChange("brandVoice", e.target.value)}
          disabled={!isEditing}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: `${theme.primaryColor}40`,
              },
              "&:hover fieldset": {
                borderColor: theme.primaryColor,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.primaryColor,
              },
            },
            "& .MuiInputLabel-root": {
              color: theme.textColor,
            },
            "& .MuiInputBase-input": {
              color: theme.textColor,
            },
          }}
        />

        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                color: theme.primaryColor,
              }}
            >
              Key Differentiators
            </Typography>
            {isEditing && (
              <Button
                onClick={addDifferentiator}
                sx={{
                  color: theme.primaryColor,
                  "&:hover": {
                    backgroundColor: `${theme.primaryColor}20`,
                  },
                }}
              >
                Add
              </Button>
            )}
          </Box>
          {isEditing ? (
            <Box>
              {editedData.keyDifferentiators.map((differentiator, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: 1,
                    alignItems: "center",
                  }}
                >
                  <TextField
                    fullWidth
                    value={differentiator}
                    onChange={(e) =>
                      handleDifferentiatorChange(index, e.target.value)
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: `${theme.primaryColor}40`,
                        },
                        "&:hover fieldset": {
                          borderColor: theme.primaryColor,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: theme.primaryColor,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: theme.textColor,
                      },
                      "& .MuiInputBase-input": {
                        color: theme.textColor,
                      },
                    }}
                  />
                  <IconButton
                    onClick={() => removeDifferentiator(index)}
                    sx={{
                      color: theme.accentColor,
                      "&:hover": {
                        backgroundColor: `${theme.accentColor}20`,
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {editedData.keyDifferentiators.map((differentiator, index) => (
                <Chip
                  key={index}
                  label={differentiator}
                  sx={{
                    backgroundColor: `${theme.primaryColor}20`,
                    color: theme.textColor,
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {isEditing && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: theme.primaryColor,
                color: theme.buttonTextColor,
                "&:hover": {
                  backgroundColor: `${theme.primaryColor}CC`,
                },
              }}
            >
              Save Changes
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BusinessContextSection;
